import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import {
  generateDisplayPickUpTimeAndDropOffTime,
  mergeServiceTypeAndSpecialServiceType,
} from 'helper/anytime-booking';
import React, { useMemo, useEffect, useState } from 'react';
import usePrevious from 'common/usePrevious';
import ScalableImg from 'common/scalable-img';
import { connect } from 'react-redux';
import cx from 'classnames';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import { SCRIBE_STATE_NAMES } from 'applications/scribe/instrumentations/track';
import { deliveryDetailOverlayVisibleSelector } from 'selectors/new-booking-selectors';
import deliveryOptionIcon from 'public/icons/delivery-option.svg';
import { getUserCountryCodeFromState } from 'selectors/app-selector';
import styles from './styles.less';

export const useServiceConfig = (delivery, countryCode) =>
  useMemo(
    () => {
      const { detail: deliveryDetail, config } = delivery;
      const selectedServiceType = _get(
        deliveryDetail,
        'configuration.serviceType',
      );
      const selectedSpecialServiceType = _get(
        deliveryDetail,
        'configuration.specialServiceType',
      );
      const scheduledPickupSlot = _get(
        deliveryDetail,
        'configuration.scheduledPickupSlot',
      );
      const serviceType =
        config &&
        config.serviceTypes &&
        config.serviceTypes.find(st => st.id === selectedServiceType);
      const specialServiceType =
        serviceType &&
        serviceType.specialServices &&
        serviceType.specialServices.find(
          sst => sst.id === selectedSpecialServiceType,
        );
      if (!serviceType || !specialServiceType) {
        return {
          serviceType: undefined,
          time: {},
        };
      }
      const st = mergeServiceTypeAndSpecialServiceType(
        serviceType,
        specialServiceType,
      );
      const time = generateDisplayPickUpTimeAndDropOffTime(
        serviceType,
        specialServiceType,
        scheduledPickupSlot,
        countryCode,
      );
      return {
        serviceType: st,
        time,
      };
    },
    [delivery, countryCode],
  );

const ServiceConfigurationField = ({
  className,
  delivery,
  onClick,
  detailOverlayVisible,
  stateName,
  countryCode,
  isFirstDelivery = false,
}) => {
  const { detail: deliveryDetail, config, isRebook } = delivery;
  const pickUp = _get(deliveryDetail, 'pickUp.poi');
  const dropOff = _get(deliveryDetail, 'dropOffs[0].poi');
  const selectedServiceType = _get(deliveryDetail, 'configuration.serviceType');
  const selectedSpecialServiceType = _get(
    deliveryDetail,
    'configuration.specialServiceType',
  );
  const scheduledPickupSlot = _get(
    deliveryDetail,
    'configuration.scheduledPickupSlot',
  );
  const disabled = !pickUp || !dropOff;

  // automatically open the service option sheet
  const previousDisabled = usePrevious(disabled);
  const [autoOpenOnce, setAutoOpenOnce] = useState(false);
  useEffect(
    () => {
      if (
        (!disabled && previousDisabled) ||
        (isRebook &&
          stateName === SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB &&
          isFirstDelivery)
      ) {
        setAutoOpenOnce(true);
      }
    },
    [
      disabled,
      previousDisabled,
      setAutoOpenOnce,
      isRebook,
      stateName,
      isFirstDelivery,
    ],
  );
  useEffect(
    () => {
      if (autoOpenOnce && onClick && !detailOverlayVisible) {
        setAutoOpenOnce(false);
        setTimeout(() => {
          onClick();
        }, 200);
      }
    },
    [autoOpenOnce, setAutoOpenOnce, onClick, detailOverlayVisible],
  );

  const { iconClass, iconSrc, firstRowText, secondRowText } = useMemo(
    () => {
      const serviceType =
        config &&
        config.serviceTypes &&
        config.serviceTypes.find(st => st.id === selectedServiceType);
      const specialServiceType =
        serviceType &&
        serviceType.specialServices &&
        serviceType.specialServices.find(
          sst => sst.id === selectedSpecialServiceType,
        );

      if (serviceType && specialServiceType) {
        const st = mergeServiceTypeAndSpecialServiceType(
          serviceType,
          specialServiceType,
        );
        const times = generateDisplayPickUpTimeAndDropOffTime(
          serviceType,
          specialServiceType,
          scheduledPickupSlot,
          countryCode,
        );
        return {
          iconClass: styles.biggerIcon,
          iconSrc: st.icon || deliveryOptionIcon,
          firstRowText: `${I18n.t('v2.anytimeBooking.pickUpAt')} ${
            times.pickUpTime
          }`,
          secondRowText: `${I18n.t('v2.anytimeBooking.dropOffBy')} ${
            times.dropOffTime
          } • ${st.name}`,
        };
      }
      return {
        iconClass: styles.icon,
        iconSrc: deliveryOptionIcon,
        firstRowText: I18n.t('v2.anytimeBooking.chooseDeliveryOptions'),
      };
    },
    [
      selectedServiceType,
      selectedSpecialServiceType,
      scheduledPickupSlot,
      config,
    ],
  );

  return (
    <div className={cx(styles.serviceConfigField, className)}>
      <div
        onClick={disabled ? undefined : onClick}
        className={cx(styles.content, { [styles.disabled]: disabled })}
      >
        <ScalableImg className={iconClass} src={iconSrc} alt="" />
        <div className={styles.selectedOption}>
          <div className={styles.firstRow}>{firstRowText}</div>
          {secondRowText && (
            <div className={styles.secondRow}>{secondRowText}</div>
          )}
        </div>
      </div>
    </div>
  );
};

ServiceConfigurationField.defaultProps = {
  className: undefined,
  detailOverlayVisible: false,
  isFirstDelivery: false,
  countryCode: undefined,
};

ServiceConfigurationField.propTypes = {
  stateName: PropTypes.string.isRequired,
  className: PropTypes.string,
  delivery: NewBookingDeliveryPropType.isRequired,
  onClick: PropTypes.func.isRequired,
  detailOverlayVisible: PropTypes.bool,
  isFirstDelivery: PropTypes.bool,
  countryCode: PropTypes.string,
};

export default connect(state => ({
  detailOverlayVisible: deliveryDetailOverlayVisibleSelector(state),
  countryCode: getUserCountryCodeFromState(state),
}))(ServiceConfigurationField);
